import React from "react";
import { VehicleForm } from "./VehicleForm";
import Modal from "@/components/ui/Modal";
import {
  useCreateVehicle,
  useUpdateVehicle,
  useVehicle,
  useVehicleForm,
} from "@/hooks/useVehicles";
import { Vehicle, CreateVehicleRequest } from "@/types";
import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "react-i18next";
import { formatUserName } from "@/utils/userName";

interface VehicleModalProps {
  isOpen: boolean;
  onClose: () => void;
  vehicle?: Vehicle;
  mode: "create" | "edit" | "view";
}

export const VehicleModal: React.FC<VehicleModalProps> = ({
  isOpen,
  onClose,
  vehicle,
  mode,
}) => {
  const { t } = useTranslation(["pages", "common"]);
  const { user } = useAuth();
  const createVehicleMutation = useCreateVehicle();
  const updateVehicleMutation = useUpdateVehicle();

  // Fetch complete vehicle details when in view or edit mode
  const { data: completeVehicle, isLoading: isLoadingVehicle } = useVehicle(
    isOpen && (mode === "view" || mode === "edit") && vehicle?.id
      ? vehicle.id
      : "",
  );

  // Use complete vehicle data if available, otherwise fall back to passed vehicle
  const displayVehicle = completeVehicle || vehicle;

  const { vehicleData, errors, validate, updateField, resetForm } =
    useVehicleForm(mode !== "create" ? displayVehicle : undefined);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate() || !user) {
      return;
    }

    try {
      if (mode === "create") {
        const createData: CreateVehicleRequest = {
          year: parseInt(vehicleData.year, 10),
          maker: vehicleData.maker,
          model: vehicleData.model,
          userId: user.id,
          name: vehicleData.name || undefined,
          description: vehicleData.description || undefined,
        };
        await createVehicleMutation.mutateAsync(createData);
      } else if (mode === "edit" && displayVehicle) {
        await updateVehicleMutation.mutateAsync({
          id: displayVehicle.id,
          year: parseInt(vehicleData.year, 10),
          maker: vehicleData.maker,
          model: vehicleData.model,
          name: vehicleData.name || undefined,
          description: vehicleData.description || undefined,
        });
      }

      onClose();
    } catch (error) {
      console.error("Error saving vehicle:", error);
    }
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  const getModalTitle = () => {
    switch (mode) {
      case "create":
        return t("pages:vehicles.modal.createTitle");
      case "edit":
        return t("pages:vehicles.modal.editTitle");
      case "view":
        return t("pages:vehicles.modal.viewTitle");
      default:
        return t("pages:vehicles.modal.defaultTitle");
    }
  };

  const formatVehicleTitle = (vehicle: Vehicle) => {
    if (vehicle.name) {
      return vehicle.name;
    }
    return `${vehicle.year} ${vehicle.maker} ${vehicle.model}`;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={getModalTitle()} size="lg">
      {mode === "view" && displayVehicle ? (
 <div className="space-y-6">
          {isLoadingVehicle ? (
 <div className="bg-secondary-50 rounded-lg p-6">
 <div className="animate-pulse space-y-4">
 <div className="h-6 bg-secondary-200 rounded w-3/4"></div>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div className="h-4 bg-secondary-200 rounded w-1/2"></div>
 <div className="h-4 bg-secondary-200 rounded w-1/2"></div>
                </div>
 <div className="h-4 bg-secondary-200 rounded w-2/3"></div>
              </div>
            </div>
          ) : (
            <div className="bg-secondary-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">
                {formatVehicleTitle(displayVehicle)}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm font-medium text-secondary-700">
                    {t("pages:vehicles.modal.vehicle")}
                  </p>
                  <p className="text-secondary-900">
                    {displayVehicle.year} {displayVehicle.maker}{" "}
                    {displayVehicle.model}
                  </p>
                </div>
                {displayVehicle.name && (
                  <div>
                    <p className="text-sm font-medium text-secondary-700">
                      {t("pages:vehicles.modal.customName")}
                    </p>
                    <p className="text-secondary-900">{displayVehicle.name}</p>
                  </div>
                )}
                {displayVehicle.user && (
                  <div>
                    <p className="text-sm font-medium text-secondary-700">
                      {t("pages:vehicles.modal.owner")}
                    </p>
                    <p className="text-secondary-900">
                      {formatUserName(displayVehicle.user)}
                    </p>
                  </div>
                )}
              </div>

              {displayVehicle.description && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-secondary-700 mb-2">
                    {t("common:description")}
                  </p>
                  <p className="text-secondary-900">{displayVehicle.description}</p>
                </div>
              )}

              {displayVehicle.vehicleModel?.description && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-secondary-700 mb-2">
                    {t("pages:vehicles.modal.modelDescription")}
                  </p>
                  <p className="text-secondary-900">
                    {displayVehicle.vehicleModel.description}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-secondary-600">
                <div>
                  <p className="font-medium">
                    {t("pages:vehicles.modal.created")}
                  </p>
                  <p>
                    {new Date(displayVehicle.createdAt).toLocaleDateString()}
                  </p>
                </div>
                {displayVehicle.updatedAt && (
                  <div>
                    <p className="font-medium">
                      {t("pages:vehicles.modal.lastUpdated")}
                    </p>
                    <p>
                      {new Date(displayVehicle.updatedAt).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-secondary-600 text-white rounded-lg hover:bg-secondary-700 transition-colors"
            >
              {t("common:close")}
            </button>
          </div>
        </div>
      ) : mode === "edit" && isLoadingVehicle ? (
 <div className="bg-secondary-50 rounded-lg p-6">
 <div className="animate-pulse space-y-4">
 <div className="h-6 bg-secondary-200 rounded w-3/4"></div>
 <div className="space-y-3">
 <div className="h-4 bg-secondary-200 rounded w-1/2"></div>
 <div className="h-10 bg-secondary-200 rounded"></div>
            </div>
 <div className="space-y-3">
 <div className="h-4 bg-secondary-200 rounded w-1/2"></div>
 <div className="h-10 bg-secondary-200 rounded"></div>
            </div>
 <div className="space-y-3">
 <div className="h-4 bg-secondary-200 rounded w-1/2"></div>
 <div className="h-20 bg-secondary-200 rounded"></div>
            </div>
          </div>
        </div>
      ) : (
        <VehicleForm
          vehicleData={vehicleData}
          errors={errors}
          onUpdateField={updateField}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isSubmitting={
            createVehicleMutation.isPending || updateVehicleMutation.isPending
          }
          isEditing={mode === "edit"}
        />
      )}
    </Modal>
  );
};
